import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { CollectionStore, useComboboxContext } from '@ariakit/react'
import { clickedMentionSuggestion, mentionPrompt } from '@components/Analytics/performanceAnalytics'
import { Avatar } from '@components/Avatar/Avatar'
import { useEditorSlashCommandData } from '@components/RichTextPlate/hooks/editorHooks'
import { withRef } from '@udecode/cn'
import { Value } from '@udecode/plate-common'
import { PlateElement, TPlateEditor } from '@udecode/plate-common/react'
import { Box, Icon, Stack, Text } from '@ui'
import { sortBy } from 'lodash-es'
import { atoms } from 'ui/styles/atoms.css'
import { itemShimmerClass } from 'ui/styles/globals/shimmer.css'
import { shortAddress } from 'ui/utils/utils'
import { Channel, useBotData, useChannelId } from 'use-towns-client'
import { isOfficialTownsToken } from 'utils/isOfficialTownsToken'

import { ComboboxContainer } from './ComboboxContainer'
import { ComboboxIcon } from './ComboboxIcon'
import { ComboboxTrailingContent } from './ComboboxTrailingContent'
import { ComboboxTrailingTickerContent } from './ComboboxTrailingTickerContent'
import { emojiSearch } from './emoji-search'
import {
    channelMentionFilter,
    getTriggerType,
    getUsernameForMention,
    isChannelCombobox,
    isEmojiCombobox,
    isSlashCommandCombobox,
    isTickerCombobox,
    isUserCombobox,
    onMentionSelectTriggerMap,
} from './helpers'
import { InlineComboboxItem } from './InlineCombobox'
import {
    ComboboxContainerProps,
    ComboboxInputUserProps,
    ComboboxTypes,
    TComboboxItemWithData,
    TMentionTicker,
    TSlashCommand,
    TUserWithChannel,
} from './types'
import { useSearchTokens } from './useSearchTokens'

type CollectionStoreItem = ReturnType<CollectionStore['item']>

/** Combobox item UI for channels and @user mentions */
export const ComboboxItemUser = ({
    item,
    editor,
    trigger,
}: {
    trigger: string
    editor: TPlateEditor<Value>
    item: TComboboxItemWithData<TUserWithChannel>
}) => {
    const getItem = useCallback(
        (_storeProps: CollectionStoreItem) => Object.assign({}, _storeProps, item),
        [item],
    )

    return (
        <InlineComboboxItem
            key={item.key}
            value={item.text}
            labelIcon={
                item.data.isBot ? <Icon type="robot" size="square_xs" color="gray2" /> : undefined
            }
            getItem={getItem}
            secondaryText={getUsernameForMention<TUserWithChannel>(ComboboxTypes.userMention, item)}
            Icon={<ComboboxIcon item={item.data} comboboxType={ComboboxTypes.userMention} />}
            trailingContent={
                <ComboboxTrailingContent
                    userId={item.data.userId}
                    isChannelMember={item.data.isChannelMember}
                />
            }
            onClick={() => {
                onMentionSelectTriggerMap(trigger)?.(editor, item, item.text)
                if (isUserCombobox(trigger)) {
                    clickedMentionSuggestion()
                }
            }}
        />
    )
}

export const ComboboxItemTicker = ({
    item,
    editor,
    trigger,
}: {
    trigger: string
    editor: TPlateEditor<Value>
    item: TComboboxItemWithData<TMentionTicker>
}) => {
    const getItem = useCallback(
        (_storeProps: CollectionStoreItem) => Object.assign({}, _storeProps, item),
        [item],
    )
    return (
        <InlineComboboxItem
            key={item.key}
            value={item.data.name}
            getItem={getItem}
            Icon={<TickerIcon item={item.data} />}
            trailingContent={<ComboboxTrailingTickerContent item={item.data} />}
            onClick={() => {
                onMentionSelectTriggerMap(trigger)?.(editor, item, item.text)
            }}
        >
            <Stack width="150">
                <Box horizontal>
                    <Text truncate color="default" fontSize="sm">
                        {item.data.name}
                    </Text>
                    {isOfficialTownsToken(item.data.address) && (
                        <Icon type="verifiedEnsName" size="square_xs" />
                    )}
                </Box>
                <Text truncate grow color="gray2" fontSize="xs">
                    <span className={atoms({ color: 'default', fontWeight: 'strong' })}>
                        {item.data.symbol}
                    </span>{' '}
                    {shortAddress(item.data.address)}
                </Text>
            </Stack>
        </InlineComboboxItem>
    )
}

const TickerIcon = ({ item }: { item: TMentionTicker }) => {
    return item.imageUrl.length > 0 ? (
        <Box as="img" src={item.imageUrl} square="square_md" rounded="full" />
    ) : item.chain === '8453' ? (
        <Icon size="square_md" type="baseEth" />
    ) : (
        <Icon size="square_md" type="solana" />
    )
}

const SlashCommandSkeleton = () => {
    const nameWidth = useMemo(() => Math.floor(Math.random() * (120 - 80) + 80), [])
    const botNameWidth = useMemo(() => Math.floor(Math.random() * (100 - 60) + 60), [])
    const descriptionWidth = useMemo(() => Math.floor(Math.random() * (250 - 150) + 150), [])
    return (
        <Stack horizontal gap padding="sm" paddingRight="sm" width="100%" alignItems="center">
            <Box square="square_md" rounded="full" className={itemShimmerClass} />
            <Stack gap="xs">
                <Box
                    style={{ width: nameWidth }}
                    height="x2"
                    rounded="xs"
                    className={itemShimmerClass}
                />
                <Stack horizontal gap="sm">
                    <Box
                        style={{ width: botNameWidth }}
                        height="x2"
                        rounded="xs"
                        className={itemShimmerClass}
                    />
                    <Box
                        style={{ width: descriptionWidth }}
                        height="x2"
                        rounded="xs"
                        className={itemShimmerClass}
                    />
                </Stack>
            </Stack>
        </Stack>
    )
}

export const ComboboxItemSlashCommand = ({
    item,
    editor,
    trigger,
}: {
    trigger: string
    editor: TPlateEditor<Value>
    item: TComboboxItemWithData<TSlashCommand>
}) => {
    const getItem = useCallback(
        (_storeProps: CollectionStoreItem) => Object.assign({}, _storeProps, item),
        [item],
    )

    const { data: bot } = useBotData(item.data.appAddress)

    return (
        <InlineComboboxItem
            key={item.key}
            value={item.data.name}
            getItem={getItem}
            onClick={() => {
                onMentionSelectTriggerMap(trigger)?.(editor, item, item.text)
            }}
        >
            <Stack horizontal gap paddingRight="sm" alignItems="center">
                <Avatar dontAnimateGreenDot size="avatar_sm" src={bot?.metadata?.avatarUrl} />
                <Stack>
                    <Text truncate color="default">
                        /{item.data.name}
                    </Text>
                    <Text truncate color="gray2" fontSize="sm">
                        <span className={atoms({ fontWeight: 'strong' })}>
                            {bot?.metadata?.displayName}
                        </span>
                        {bot?.metadata?.displayName && item.data.description && ' · '}
                        {item.data.description}
                    </Text>
                </Stack>
            </Stack>
        </InlineComboboxItem>
    )
}

/** Combobox item UI for channels and emojis mentions */
export const ComboboxItemGeneric = ({
    item,
    editor,
    trigger,
}: {
    editor: TPlateEditor<Value>
    item: TComboboxItemWithData<Channel>
    trigger: string
}) => {
    const getItem = useCallback(
        (_storeProps: CollectionStoreItem) => Object.assign({}, _storeProps, item),
        [item],
    )
    return (
        <InlineComboboxItem
            key={item.key}
            value={item.text}
            getItem={getItem}
            Icon={
                <ComboboxIcon
                    item={item.data}
                    comboboxType={
                        isChannelCombobox(trigger)
                            ? ComboboxTypes.channelMention
                            : ComboboxTypes.emojiMention
                    }
                />
            }
            onClick={() => onMentionSelectTriggerMap(trigger)?.(editor, item, item.text)}
        />
    )
}

const EMPTY_ARRAY: TComboboxItemWithData<TUserWithChannel | Channel>[] = []
// TODO: virtualized list instead of slicing the array
const MAX_USER_MENTIONS_RENDERED = 20

export const ComboboxInput = withRef<typeof PlateElement, ComboboxInputUserProps>(
    ({ className, getUserMentions, getChannelMentions, getTickerMentions, ...props }, ref) => {
        const {
            query: searchString,
            editor,
            element: { trigger: propTrigger },
        } = props as ComboboxContainerProps
        const [, startTransition] = useTransition()

        const streamId = useChannelId()
        const trigger = useRef<string>(propTrigger as string)
        const [filteredItems, setFilteredItems] = useState<TComboboxItemWithData[]>(EMPTY_ARRAY)

        const [showTickerResults, setShowTickerResults] = useState(false)
        useEffect(() => {
            if (!isTickerCombobox(trigger.current)) {
                return
            }
            const timer = setTimeout(() => {
                setShowTickerResults(true)
            }, 500)
            return () => clearTimeout(timer)
        }, [])

        const { slashCommands: fetchedSlashCommands, isLoadingSlashCommands } =
            useEditorSlashCommandData(streamId, {
                enabled: !!streamId && isSlashCommandCombobox(trigger.current),
            })

        const isDraftMode = !streamId && isSlashCommandCombobox(trigger.current)

        const store = useComboboxContext()!
        const { data: tokenList } = useSearchTokens({
            searchString,
            enabled: isTickerCombobox(trigger.current) && showTickerResults,
        })

        // Check if ticker search starts with a digit (e.g., $100 should not show results)
        const tickerStartsWithDigit = isTickerCombobox(trigger.current) && /^\d/.test(searchString)

        const getAllItems = useCallback(() => {
            if (isEmojiCombobox(trigger.current)) {
                return EMPTY_ARRAY
            }
            if (isTickerCombobox(trigger.current)) {
                // Return empty during delay period or if search starts with digit (dollar amounts)
                if (!showTickerResults || tickerStartsWithDigit) {
                    return EMPTY_ARRAY
                }
                return tokenList
            }
            if (isSlashCommandCombobox(trigger.current)) {
                return fetchedSlashCommands
            }
            return isUserCombobox(trigger.current) ? getUserMentions() : getChannelMentions()
        }, [
            getUserMentions,
            getChannelMentions,
            tokenList,
            fetchedSlashCommands,
            showTickerResults,
            tickerStartsWithDigit,
        ])

        useEffect(() => {
            startTransition(() => {
                setFilteredItems(getAllItems())
            })
        }, [getAllItems, setFilteredItems, fetchedSlashCommands])

        useEffect(() => {
            if (!isEmojiCombobox(trigger.current) && !Array.isArray(getAllItems())) {
                return
            }

            if (!searchString || searchString.length === 0) {
                startTransition(() => {
                    setFilteredItems(getAllItems())
                })
                return
            }

            let _filteredItemList: TComboboxItemWithData<
                TUserWithChannel | Channel | TMentionTicker | TSlashCommand
            >[] = EMPTY_ARRAY
            if (isEmojiCombobox(trigger.current)) {
                emojiSearch(searchString).then((emojiSearchResult) => {
                    startTransition(() => {
                        setFilteredItems(emojiSearchResult)
                    })
                })
                return
            } else if (isTickerCombobox(trigger.current)) {
                // Return empty during delay period or if search starts with digit (dollar amounts)
                _filteredItemList =
                    showTickerResults && !tickerStartsWithDigit ? (tokenList ?? []) : []
            } else if (isSlashCommandCombobox(trigger.current)) {
                const lowerSearch = searchString.toLowerCase()
                _filteredItemList = sortBy(
                    // Only filter by command name, not description
                    fetchedSlashCommands.filter((cmd) =>
                        cmd.data.name.toLowerCase().includes(lowerSearch),
                    ),
                    [
                        // Rank: 0 = exact match, 1 = prefix match, 2 = substring match
                        (cmd) => {
                            const name = cmd.data.name.toLowerCase()
                            if (name === lowerSearch) {
                                return 0
                            }
                            if (name.startsWith(lowerSearch)) {
                                return 1
                            }
                            return 2
                        },
                        (cmd) => cmd.data.name.toLowerCase(),
                    ],
                )
            } else if (isUserCombobox(trigger.current)) {
                _filteredItemList = getUserMentions(searchString)
            } else {
                _filteredItemList = getChannelMentions().filter(channelMentionFilter(searchString))
            }
            startTransition(() => {
                setFilteredItems(_filteredItemList)
            })
        }, [
            getAllItems,
            searchString,
            store,
            getUserMentions,
            getChannelMentions,
            getTickerMentions,
            tokenList,
            fetchedSlashCommands,
            showTickerResults,
            tickerStartsWithDigit,
        ])

        // Track analytics event when user types "@"
        useEffect(() => {
            if (isUserCombobox(trigger.current)) {
                mentionPrompt()
            }
        }, [])

        const searchResults = useMemo(() => {
            let itemsToRender = filteredItems

            const kind = getTriggerType(trigger.current)
            if (kind === ComboboxTypes.slashCommand && (isLoadingSlashCommands || isDraftMode)) {
                return {
                    kind: ComboboxTypes.slashCommand,
                    items: Array.from({ length: 4 }, (_, i) => <SlashCommandSkeleton key={i} />),
                }
            }

            // Only render the first MAX_USER_MENTIONS_RENDERED to avoid performance issues
            // with hundreds of Avatar components mounting and triggering expensive user lookups
            if (kind === ComboboxTypes.userMention) {
                itemsToRender = itemsToRender.slice(0, MAX_USER_MENTIONS_RENDERED)
            }

            return {
                kind,
                items: itemsToRender.map((item) => {
                    if (kind === ComboboxTypes.userMention) {
                        return (
                            <ComboboxItemUser
                                key={item.key}
                                editor={editor}
                                trigger={trigger.current}
                                item={item as TComboboxItemWithData<TUserWithChannel>}
                            />
                        )
                    } else if (kind === ComboboxTypes.tickerMention) {
                        return (
                            <ComboboxItemTicker
                                key={item.key}
                                editor={editor}
                                trigger={trigger.current}
                                item={item as TComboboxItemWithData<TMentionTicker>}
                            />
                        )
                    } else if (kind === ComboboxTypes.slashCommand) {
                        const commandItem = item as TComboboxItemWithData<TSlashCommand>
                        return (
                            <ComboboxItemSlashCommand
                                key={commandItem.key}
                                editor={editor}
                                trigger={trigger.current}
                                item={commandItem}
                            />
                        )
                    } else {
                        return (
                            <ComboboxItemGeneric
                                key={item.key}
                                editor={editor}
                                trigger={trigger.current}
                                item={item as TComboboxItemWithData<Channel>}
                            />
                        )
                    }
                }),
            } as const
        }, [filteredItems, isLoadingSlashCommands, isDraftMode, editor])

        return (
            <ComboboxContainer
                {...props}
                ref={ref}
                query={searchString}
                resultsLength={filteredItems.length}
                align={searchResults.kind === ComboboxTypes.slashCommand ? 'end' : 'center'}
                searchResults={searchResults.items}
                isLoading={
                    isSlashCommandCombobox(trigger.current) &&
                    (isLoadingSlashCommands || isDraftMode)
                }
            />
        )
    },
)
